import { useEffect } from "react";
import HeroSectionVerseFinder from "@/components/HeroSectionVerseFinder";
import VerseFinderForm from "@/components/VerseFinderForm";
import LoadingScriptures from "@/components/LoadingScriptures";
import ScriptureResults from "@/components/ScriptureResults";
import { useFindScripture } from "@/hooks/useFindScripture";
import { useScrollToNewContent } from "@/hooks/useScrollToNewContent";
import { updateAllMetadata } from "@/utils/dynamicMetadata";
import { useMetadata } from "@/hooks/useMetadata";
import { useScrollToTop } from "@/hooks/useScrollToTop"; // Import the hook

const FindYourVerse = () => {
  // Use the scroll to top hook to ensure page starts at top every time
  useScrollToTop();

  const {
    prompt,
    setPrompt,
    isLoading,
    isSaving,
    scriptures,
    loadingStates,
    handleSubmit,
    getCommentary,
    getGuidance,
    saveScriptures,
    saveItem
  } = useFindScripture();

  // Create ref for auto-scrolling when scriptures are loaded
  const scrollRef = useScrollToNewContent(scriptures, {
    condition: !isLoading && scriptures.length > 0,
    delay: 300
  });

  // Initialize page metadata with the useMetadata hook
  useMetadata({
    title: "Selah | Find Your Verse",
    description: "Discover Bible verses that speak to your current needs, questions, or circumstances.",
    path: "/find-your-verse",
    type: "website"
  });

  // Update metadata when scriptures are loaded
  useEffect(() => {
    if (scriptures.length > 0 && prompt) {
      // Update metadata to reflect current search
      updateAllMetadata({
        title: `Selah | Scripture for "${prompt}"`,
        description: `Bible verses related to: ${prompt}. Find scriptures that speak to your situation.`,
        type: "article"
      });
    }
  }, [scriptures.length, prompt]);

  return (
    <div className="min-h-screen pb-16">
      <HeroSectionVerseFinder />
      
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <VerseFinderForm
          prompt={prompt}
          setPrompt={setPrompt}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
        />
        
        {isLoading ? (
          <LoadingScriptures />
        ) : (
          <>
            {scriptures.length > 0 && (
              <div ref={scrollRef}>
                <ScriptureResults
                  scriptures={scriptures}
                  loadingStates={loadingStates}
                  onGetCommentary={getCommentary}
                  onGetGuidance={getGuidance}
                  onSaveAll={saveScriptures}
                  onSaveItem={saveItem}
                  isSaving={isSaving}
                  prompt={prompt}
                  autoExpandContent={true}
                  hideGuidance={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FindYourVerse;
